import { useMemo } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { KIND_ID_PATTERN, type Domain, type KindDef, type Vocabulary } from '@/schema'

/**
 * The vocabulary registry as the UI reads it (§4.4, §9.1).
 *
 * The editor legend, the `1–9` kind shortcuts, the Properties panel and the
 * library's kind dots all read this store instead of a hardcoded list —
 * that is what "data-driven kinds" means in practice. `api/vocab.ts` fills
 * it from `GET /api/vocab` and writes every mutation's answer back through
 * `upsertKind`, so a freshly added kind is usable before the refetch lands.
 *
 * Alias resolution is done here too: a deprecated kind with `alias_of`
 * resolves to its successor, so a document authored against an older
 * `vocab_rev` still renders with a colour and a label.
 */

/** Ordered list per domain plus an id index, kept in sync by every mutator. */
interface DomainIndex {
  order: string[]
  byId: Record<string, KindDef>
}

export interface VocabState {
  /** `settings.vocab_rev` of the loaded registry; 0 before the first load. */
  rev: number
  loaded: boolean
  domains: Partial<Record<Domain, DomainIndex>>

  setVocabulary(v: Vocabulary): void
  /** Write one row (a mutation's answer). `bumpRev` mirrors the server's own bump. */
  upsertKind(kind: KindDef, opts?: { bumpRev?: boolean }): void
  removeKind(domain: Domain, id: string): void
  setRev(rev: number): void
  reset(): void
}

const EMPTY: KindDef[] = []

function indexOf(kinds: KindDef[]): DomainIndex {
  const sorted = [...kinds].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id))
  const byId: Record<string, KindDef> = {}
  for (const k of sorted) byId[k.id] = k
  return { order: sorted.map((k) => k.id), byId }
}

export const useVocabStore = create<VocabState>()((set, get) => ({
  rev: 0,
  loaded: false,
  domains: {},

  setVocabulary: (v) => {
    const domains: Partial<Record<Domain, DomainIndex>> = {}
    for (const [domain, kinds] of Object.entries(v.domains ?? {})) {
      domains[domain as Domain] = indexOf(kinds ?? [])
    }
    set({ rev: v.rev ?? 0, loaded: true, domains })
  },

  upsertKind: (kind, opts = {}) => {
    const prev = get().domains[kind.domain]
    const rows = prev ? prev.order.map((id) => prev.byId[id]).filter((k): k is KindDef => Boolean(k)) : []
    const next = rows.filter((k) => k.id !== kind.id)
    next.push(kind)
    set((s) => ({
      rev: opts.bumpRev ? s.rev + 1 : s.rev,
      loaded: true,
      domains: { ...s.domains, [kind.domain]: indexOf(next) },
    }))
  },

  removeKind: (domain, id) => {
    const prev = get().domains[domain]
    if (!prev) return
    const next = prev.order
      .filter((x) => x !== id)
      .map((x) => prev.byId[x])
      .filter((k): k is KindDef => Boolean(k))
    set((s) => ({ rev: s.rev + 1, domains: { ...s.domains, [domain]: indexOf(next) } }))
  },

  setRev: (rev) => set({ rev }),
  reset: () => set({ rev: 0, loaded: false, domains: {} }),
}))

/* ------------------------------------------------------------- selectors */

/** Every row of a domain in `sort_order`, deprecated rows included. */
export function kindsOf(state: VocabState, domain: Domain): KindDef[] {
  const idx = state.domains[domain]
  if (!idx) return EMPTY
  return idx.order.map((id) => idx.byId[id]).filter((k): k is KindDef => Boolean(k))
}

/** The rows a picker should offer: enabled and not deprecated. */
export function activeKindsOf(state: VocabState, domain: Domain): KindDef[] {
  return kindsOf(state, domain).filter((k) => k.enabled && !k.deprecated)
}

/** One row by id, following `alias_of` (bounded, so a cycle cannot hang the UI). */
export function resolveKind(state: VocabState, domain: Domain, id: string): KindDef | null {
  const idx = state.domains[domain]
  if (!idx) return null
  let cur = idx.byId[id] ?? null
  for (let hops = 0; cur?.alias_of && hops < 8; hops++) {
    const next = idx.byId[cur.alias_of]
    if (!next || next.id === cur.id) break
    cur = next
  }
  return cur
}

/** Hook form of `resolveKind` for components that only need one kind. */
export function useKind(domain: Domain, id: string | undefined): KindDef | null {
  return useVocabStore((s) => (id ? resolveKind(s, domain, id) : null))
}

/**
 * Hook form of `kindsOf`; `active` drops disabled/deprecated rows.
 *
 * The selector returns the **stable index object**, never a freshly built
 * array — zustand 5 compares snapshots by reference, and a selector that
 * allocates on every call loops forever.
 */
export function useKinds(domain: Domain, opts: { active?: boolean } = {}): KindDef[] {
  const idx = useVocabStore((s) => s.domains[domain])
  const active = opts.active ?? false
  return useMemo(() => {
    if (!idx) return EMPTY
    const rows = idx.order.map((id) => idx.byId[id]).filter((k): k is KindDef => Boolean(k))
    return active ? rows.filter((k) => k.enabled && !k.deprecated) : rows
  }, [idx, active])
}

/**
 * Colour for a kind: the registry's own value, else a stable generated one.
 * An unknown kind in a document is valid and inert (§4.4.4 rule 4), so it
 * still needs a colour — it gets the generated one and a `W_UNKNOWN_KIND`
 * pill elsewhere, never a crash.
 */
export function colorOf(state: VocabState, domain: Domain, id: string): string {
  const kind = resolveKind(state, domain, id)
  return kind?.color ?? autoColor(id)
}

/** One digit key and the kind it picks. */
export interface DigitKey {
  key: string
  kind: KindDef
}

/**
 * The ONE rule for the `1–9` kind keys, used by the annotator, the shortcut sheet and
 * Settings › Vocabulary alike (they used to disagree):
 *
 *  1. `pins` (a person's own favourites, in their order) take the first keys;
 *  2. a kind whose vocabulary `shortcut` is a digit keeps that digit when it is still free;
 *  3. the rest fill the free digits in `sort_order`.
 *
 * Only enabled, non-deprecated rows get a key. Letter shortcuts are ignored here — letters are
 * the annotator's tool keys. The result is sorted by key.
 */
export function digitKeys(kinds: readonly KindDef[], pins: readonly string[] = []): DigitKey[] {
  const rows = kinds.filter((k) => k.enabled && !k.deprecated)
  const byId = new Map(rows.map((k) => [k.id, k]))
  const slots: (KindDef | undefined)[] = new Array(9).fill(undefined)
  const placed = new Set<string>()
  const put = (i: number, k: KindDef) => {
    slots[i] = k
    placed.add(k.id)
  }
  let next = 0
  for (const id of pins) {
    const k = byId.get(id)
    if (!k || placed.has(k.id)) continue
    if (next > 8) break
    put(next++, k)
  }
  for (const k of rows) {
    if (placed.has(k.id) || !k.shortcut || !/^[1-9]$/.test(k.shortcut)) continue
    const i = Number(k.shortcut) - 1
    if (!slots[i]) put(i, k)
  }
  for (const k of rows) {
    if (placed.has(k.id)) continue
    const i = slots.findIndex((x) => !x)
    if (i < 0) break
    put(i, k)
  }
  const out: DigitKey[] = []
  slots.forEach((k, i) => {
    if (k) out.push({ key: String(i + 1), kind: k })
  })
  return out
}

/**
 * Every key of a domain → its kind: the `1–9` digits from {@link digitKeys} (no pins — this is
 * the shared view Settings shows) plus any letter `shortcut` a row declares (non-stage domains
 * use letters). Keys are strings so `event.key` can index the map directly.
 */
export function shortcutMap(state: VocabState, domain: Domain): Record<string, KindDef> {
  const rows = activeKindsOf(state, domain)
  const map: Record<string, KindDef> = {}
  for (const { key, kind } of digitKeys(rows)) map[key] = kind
  for (const kind of rows) if (kind.shortcut && !/^[0-9]$/.test(kind.shortcut) && !map[kind.shortcut]) map[kind.shortcut] = kind
  return map
}

/* ------------------------------------------------------------ stage domains */

/** The four vocabulary domains the stage annotator draws with. */
export const STAGE_DOMAINS = ['region_kind', 'anchor_kind', 'portal_kind', 'path_kind'] as const
export type StageDomain = (typeof STAGE_DOMAINS)[number]

export function isStageDomain(d: string): d is StageDomain {
  return (STAGE_DOMAINS as readonly string[]).includes(d)
}

/* --------------------------------------------------- pins + recent (per person) */

export interface KindPrefsState {
  /** Favourites per domain, in the order they were pinned; they take the first number keys. */
  pins: Partial<Record<Domain, string[]>>
  /** Most recent first, at most {@link RECENT_LIMIT}. */
  recent: Partial<Record<Domain, string[]>>
  togglePin(domain: Domain, id: string): void
  pushRecent(domain: Domain, id: string): void
}

export const RECENT_LIMIT = 8

/**
 * A person's own kind shortcuts, kept in this browser (`studio.kinds` in localStorage): which
 * kinds they pinned to the number keys and which they used last. Nothing here is shared or
 * sent to the server; a blocked localStorage simply means nothing is remembered.
 */
export const useKindPrefs = create<KindPrefsState>()(
  persist(
    (set) => ({
      pins: {},
      recent: {},
      togglePin: (domain, id) =>
        set((s) => {
          const cur = s.pins[domain] ?? []
          const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
          return { pins: { ...s.pins, [domain]: next } }
        }),
      pushRecent: (domain, id) =>
        set((s) => {
          const cur = s.recent[domain] ?? []
          if (cur[0] === id) return {}
          return { recent: { ...s.recent, [domain]: [id, ...cur.filter((x) => x !== id)].slice(0, RECENT_LIMIT) } }
        }),
    }),
    {
      name: 'studio.kinds',
      version: 1,
      storage: createJSONStorage(() => safeStorage()),
      partialize: (s) => ({ pins: s.pins, recent: s.recent }),
    },
  ),
)

/** localStorage when it works, else an in-memory stand-in (private windows, blocked storage, tests). */
function safeStorage(): Storage {
  try {
    const probe = '__studio_probe__'
    window.localStorage.setItem(probe, probe)
    window.localStorage.removeItem(probe)
    return window.localStorage
  } catch {
    const mem = new Map<string, string>()
    return {
      get length() {
        return mem.size
      },
      clear: () => mem.clear(),
      getItem: (k) => mem.get(k) ?? null,
      key: (i) => [...mem.keys()][i] ?? null,
      removeItem: (k) => void mem.delete(k),
      setItem: (k, v) => void mem.set(k, v),
    }
  }
}

/* ------------------------------------------------------- colour minting */

/** FNV-1a over the id: the same kind gets the same hue on every machine. */
function hashHue(id: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h % 360
}

/**
 * Auto-assigned colour for a user kind (§4.4): `oklch(0.72 0.17 <hash % 360>)`.
 * `nudgeFrom` are the hues already taken in the domain; the hue is pushed
 * away in 25° steps until it is at least 25° from each of them, so two
 * user kinds never come out looking like the same colour.
 */
export function autoColor(id: string, nudgeFrom: readonly string[] = []): string {
  const taken = nudgeFrom.map(hueOf).filter((h): h is number => h !== null)
  let hue = hashHue(id)
  for (let step = 0; step < 15; step++) {
    const clash = taken.some((t) => angularDistance(t, hue) < 25)
    if (!clash) break
    hue = (hue + 25) % 360
  }
  return `oklch(0.72 0.17 ${hue})`
}

/** Suggest a colour for a new kind in `domain`, avoiding the hues already used. */
export function suggestColor(state: VocabState, domain: Domain, id: string): string {
  return autoColor(id, kindsOf(state, domain).map((k) => k.color))
}

function angularDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % 360
  return d > 180 ? 360 - d : d
}

/** Hue of an `oklch(L C H)` string, or `null` for hex and anything else. */
function hueOf(color: string): number | null {
  const m = /oklch\(\s*[\d.]+%?\s+[\d.]+%?\s+([\d.]+)/i.exec(color)
  if (!m || m[1] === undefined) return null
  const h = Number(m[1])
  return Number.isFinite(h) ? ((h % 360) + 360) % 360 : null
}

/* ------------------------------------------------------------ id helpers */

/** English label with a graceful fall back to the id (no locale is a bug, not a crash). */
export function labelOf(kind: Pick<KindDef, 'id' | 'label'>, locale = 'en'): string {
  return kind.label?.[locale] ?? kind.label?.['en'] ?? Object.values(kind.label ?? {})[0] ?? kind.id
}

/** User-added ids MUST start with `x:` (§4.4.4 rule 1). */
export function isUserKindId(id: string): boolean {
  return id.startsWith('x:')
}

/**
 * Validate an id the operator typed for a NEW kind. Returns an error
 * sentence, or `null` when the id is acceptable.
 */
export function validateNewKindId(id: string, domain: Domain, state?: VocabState): string | null {
  const trimmed = id.trim()
  if (!trimmed) return 'Give the kind an id, for example x:lava.'
  // Blocked words are free lowercase text; every other domain wants `x:`.
  if (domain !== 'blocked_word' && !trimmed.startsWith('x:')) return 'Ids you add start with x: — for example x:lava.'
  if (!KIND_ID_PATTERN.test(trimmed)) return 'Use lowercase letters, digits and underscores.'
  if (state && state.domains[domain]?.byId[trimmed]) return 'That id already exists in this domain.'
  return null
}

/** `x:lava` → `lava` for display where the prefix is noise. */
export function bareId(id: string): string {
  return id.startsWith('x:') ? id.slice(2) : id
}
